// Helper puro: convierte el log de reps por serie + metadata en el shape
// jsonb que se persiste en sesiones.pose_analysis. Sin side effects, sin
// dependencias de Supabase ni React — testeable con datos mock.

import type { RepResult } from "./types";
import type { PoseAnalysisV1 } from "@/lib/supabase/queries/useGuardarSesionCompletada";

export interface BuildPoseAnalysisInput {
  /** Reps completadas agrupadas por serie. El outer index = numero de serie - 1. */
  repsBySerie: RepResult[][];
  detector: {
    schemaId: string;
    schemaVersion: string;
    evaluatorVersion: string;
  };
  captura: {
    userAgent: string;
  };
}

export function buildPoseAnalysis(
  input: BuildPoseAnalysisInput,
): PoseAnalysisV1 {
  const { repsBySerie, detector, captura } = input;

  // Aggregate por serie. Calidad y prominencia se promedian sobre todas las
  // reps detectadas (válidas + inválidas) de la serie — para métrica útil
  // habría que filtrar por valid, pero acá queremos visibilidad de qué pasó.
  // Las inválidas igual se separan en reps_validos vs reps_detectados.
  const series = repsBySerie.map((reps, idx) => {
    const validos = reps.filter((r) => r.valid);
    return {
      numero: idx + 1,
      reps_detectados: reps.length,
      reps_validos: validos.length,
      calidad_promedio: round2(avg(validos.map((r) => r.quality))),
      duracion_promedio_ms: Math.round(avg(reps.map((r) => r.durationMs))),
      prominencia_promedio_grados: round2(
        avg(reps.map((r) => r.prominence)),
      ),
    };
  });

  // Top 3 violaciones más frecuentes sobre toda la sesión.
  const flatReps = repsBySerie.flat();
  const counts: Record<string, number> = {};
  for (const r of flatReps) {
    for (const v of r.violations) {
      counts[v] = (counts[v] ?? 0) + 1;
    }
  }
  const totalReps = flatReps.length || 1;
  const violacionesFrecuentes = Object.entries(counts)
    .map(([tipo, count]) => ({
      tipo,
      frecuencia_pct: Math.round((count / totalReps) * 100),
    }))
    .sort((a, b) => b.frecuencia_pct - a.frecuencia_pct)
    .slice(0, 3);

  return {
    version: 1,
    detector: {
      schema_id: detector.schemaId,
      schema_version: detector.schemaVersion,
      evaluator_version: detector.evaluatorVersion,
    },
    captura: {
      user_agent: captura.userAgent,
    },
    ejecucion: { series },
    calidad_global: {
      violaciones_frecuentes: violacionesFrecuentes,
    },
  };
}

function avg(nums: number[]): number {
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
